// `slide-see-say-run`: the hand-off. "See stuff, say stuff, run stuff" as an invitation — the
// heading, the address the room plays at, and a QR for the address.
//
// **It is one slide because it was two beats saying one thing** (2026-08-13). The framing line used
// to sit between match setup and live play, and the address + QR were a separate `nav` target
// (`nav-rounds-handoff`, a kit-styled card inside `DemoLiveMatchChapters`) at the end of the match
// section. The room hears "see stuff, say stuff, run stuff" from the presenter throughout; what it
// needs on screen is the address, at the moment it can act on it. So the two are merged here, in the
// deck's own brand style on the deck's white ground, and the nav target is gone.
//
// The QR is encoded on the device from `PUBLIC_PLAY_URL` — no request is issued for it — and its box
// is reserved at full size, so the slide does not move when the image lands (or if it never does).

import { useEffect, useState, type ReactElement } from 'react';
import { SlideFrame } from './SlideFrame';
import { SLIDE_SCALE } from './scale';
import { PUBLIC_PLAY_LABEL, PUBLIC_PLAY_URL, SEE_SAY_RUN_LINE } from './content';
import './slides.css';

const QR_SIZE = 380;

export function SeeSayRunSlide(): ReactElement {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void import('qrcode')
      .then((module) => module.toDataURL(PUBLIC_PLAY_URL, { margin: 1, width: QR_SIZE }))
      .then((image) => {
        if (live) setQr(image);
      })
      // A projected slide with no QR still reads the address out loud; a thrown import must not
      // take the deck down mid-session.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return (
    <SlideFrame center>
      <h1
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.hero,
          color: 'var(--deck-ink)',
          lineHeight: 'var(--leading-tight)',
          letterSpacing: '-0.02em',
          margin: 0,
          whiteSpace: 'pre-line',
        }}
      >
        {SEE_SAY_RUN_LINE}
      </h1>
      {/* Set exactly like the starter pack's address, which is the deck's one other projected URL. */}
      <a
        href={PUBLIC_PLAY_URL}
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.title,
          color: 'var(--deck-card-border)',
        }}
      >
        <u>{PUBLIC_PLAY_LABEL}</u>
      </a>
      <div
        data-testid="see-say-run-qr-slot"
        style={{ height: QR_SIZE, width: QR_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {qr !== null && (
          <img
            data-testid="see-say-run-qr"
            src={qr}
            alt={`QR code for ${PUBLIC_PLAY_LABEL}`}
            style={{ width: '100%', height: '100%', imageRendering: 'pixelated' }}
          />
        )}
      </div>
    </SlideFrame>
  );
}
